import type { TextFieldProps } from '@mui/material'
import type { ControllerProps, FieldPath, FieldValues, PathValue } from 'react-hook-form'
import { Controller, useFormContext } from 'react-hook-form'

import type { IntegerTextFieldProps } from '@/components/inputs/IntegerTextField'
import IntegerTextField from '@/components/inputs/IntegerTextField'

export type IntegerTextFieldControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = Omit<IntegerTextFieldProps, 'name' | 'onValueChange'> & {
  controllerProps?: Omit<ControllerProps<TFieldValues, TName>, 'render' | 'name'>
  missingValue?: number
  name: TName
  onValueChange?: (value: number | undefined) => void
  showError?: boolean
  showErrorMessage?: boolean
  textFieldProps: Omit<TextFieldProps, 'render' | 'name' | 'helperText'>
}

export function IntegerTextFieldController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  controllerProps,
  missingValue,
  name,
  onValueChange,
  showError = true,
  showErrorMessage = true,
  textFieldProps,
  ...fieldProps
}: IntegerTextFieldControllerProps<TFieldValues, TName>) {
  const { control, setValue } = useFormContext<TFieldValues>()

  let onValueChangeImpl: (value: number | undefined) => void
  if (onValueChange) {
    onValueChangeImpl = onValueChange
  } else {
    onValueChangeImpl = (value: number | undefined): void =>
      setValue(name, (value ?? missingValue) as PathValue<TFieldValues, TName>)
  }

  return (
    <Controller
      control={control}
      name={name}
      {...controllerProps}
      render={({ field: { value, ...field }, fieldState: { error, invalid } }) => (
        <IntegerTextField
          textFieldProps={{
            helperText: showErrorMessage ? error?.message : undefined,
            name: name,
            ...textFieldProps,
          }}
          onValueChange={onValueChangeImpl}
          {...field}
          error={showError && invalid}
          initialValue={value == undefined ? '' : String(value)}
          {...fieldProps}
        />
      )}
    />
  )
}
